@let context = { formatters: (formatters$ | async) };
<div
  class="element-container element-container-{{ elementKey }} {{ elementActionsKeyRoot }} {{
    elementActionsKey
  }}"
  [ngStyle]="wrapperStyles"
>
  <div class="header">
    @let visibleHeaderActions = elementDataActions.visibleHeaderActions$ | async;
    <blaze-element-filter-header
      (addButtonClick)="onAddNewRow()"
      [addButtonBlocked]="isAddingRowsDisabled"
      [addButtonCssClasses]="getActionClasses(itemAdd?.styleSheetTarget ?? itemAdd?.styleSheetId)"
      [addButtonEnabled]="(itemAddVisible$ | async) ?? false"
      [addButtonLabel]="itemAdd?.label"
      [filterBuilderTriggerTpl]="filterBuilderTriggerTpl"
      [fontColor]="titleColor"
      [labelEnabled]="labelEnabled"
      [labelFontFamily]="props?.style?.titleBlock?.fontFamily"
      [labelFontSize]="props?.style?.titleBlock?.fontSize"
      [labelFontStyle]="props?.style?.titleBlock?.fontStyle"
      [labelFontWeight]="props?.style?.titleBlock?.fontWeight"
      [labelIcon]="icon"
      [labelIconAlign]="props?.content?.title?.iconPosition"
      [labelIconColor]="props?.content?.title?.iconColor?.color1"
      [labelIconSize]="props?.style?.titleBlock?.iconSize"
      [labelText]="labelText"
      [labelTextAlign]="props?.content?.title?.align"
      [searchAndFilters]="props?.content?.searchAndFilters"
      [slots]="slots"
    >
      @if (visibleHeaderActions?.length) {
        <ng-container
          [ngTemplateOutlet]="
            elementDataActions.bulkActionsGrouped ? groupedActionsTpl : headerActionsTpl
          "
          [ngTemplateOutletContext]="{
            $implicit: undefined,
            actions: visibleHeaderActions,
            cy: 'TableElement:header-actions',
            actionsHaveIcons: elementDataActions.headerActionsHaveIcons,
            isBulkAction: true,
          }"
        />
      }
    </blaze-element-filter-header>

    <ng-template #filterBuilderTriggerTpl let-fullWidth="fullWidth">
      @if (searchMode === SearchAndFiltersMode.FILTER_BUILDER) {
        <blaze-filter-builder-trigger [for]="filterBuilder" [fullWidth]="fullWidth" />
      }
    </ng-template>

    <div
      class="row"
      [class.!hidden]="
        !filterBuilder.showFilters || searchMode !== SearchAndFiltersMode.FILTER_BUILDER
      "
    >
      <blaze-filter-builder
        #filterBuilder
        (applyFilter)="applyComplexFilter($event)"
        (deleteFilter)="deleteComplexFilter($event)"
        (saveFilter)="saveComplexFilter($event)"
        [allowToCreatePublicFilters]="allowToSavePublicComplexFilters"
        [filters]="savedFilters"
        [query]="query"
        [showLoadingForSavedFilters]="savedFiltersLoading"
        [showSavedFiltersButton]="(runtimeModePublicOrSecure$ | async) !== true"
      />
    </div>
  </div>
  <div class="table-wrapper" #tableWrapper>
    <div
      [blazeScrollSync]="tableContainerScrollSync"
      [class.no-data]="!totalCount"
      [useAppTheme]="true"
      blazeVirtualScrollableElement
      class="table-container"
    >
      @let visibleRowActions = elementDataActions.visibleRowActions$ | async;
      @let hasBulkActions = !!elementDataActions.bulkActions.length;
      <div
        #table
        (matSortChange)="onSortChange($event)"
        [attr.aria-rowcount]="totalCount"
        [attr.data-sourceid]="propsDataSource?.id"
        [class.has-checks]="!elementDataActions.singleSelect()"
        [class.no-data]="!totalCount"
        [matSortActive]="sortValues.field"
        [matSortDirection]="sortValues.direction"
        [matSortDisabled]="props?.content?.table?.disableUserSort ?? false"
        class="table {{ elementKeyRoot }} {{ elementKey }} mt-32 relative"
        data-cy="TableElement:table"
        matSort
        matSortDisableClear
        role="grid"
      >
        @if (props?.content?.table?.showColumnHeaders) {
          <div
            #headerWrapper
            [style.left.px]="isHeaderFloating ? tableWrapperRect?.x : undefined"
            [style.max-width.px]="isHeaderFloating ? tableWrapperRect?.width : undefined"
            [style.width.px]="isHeaderFloating ? tableWrapperRect?.width : undefined"
            class="header-wrapper"
          >
            <be-table-header
              (resizeColumns)="resizeColumns($event)"
              (stickyChange)="isHeaderFloating = $event.isSticky"
              (toggleSelectAll)="toggleSelectAll()"
              [actionsColumnName]="actionsColumnName"
              [allColumns]="(columnsForResize$ | async) ?? []"
              [blazeStickyEnabled]="headerAlwaysVisible"
              [blazeStickyEndOffset]="rowHeightPx"
              [blazeStickyPositionOffset]="headerPositionOffset$ | async"
              [blazeStickyStartOffset]="headerStartOffset$ | async"
              [columnsVirtualScrollEnabled]="rowHeightBehavior === 'fixed'"
              [displayedColumns]="displayedColumns"
              [displayedColumnsDef]="displayedColumnsDef"
              [hasSelection]="hasBulkActions"
              [isAllSelected]="dataSource.data | isAllSelected: paginationProps : selectionCount"
              [isAnyRowSelected]="selection.hasValue()"
              [lastColumnAlwaysVisible]="props?.content?.table?.alwaysVisibleActions ?? false"
              [scrollContainer]="headerWrapper"
              [selectColumnName]="selectColumnName"
              [tableWidth]="tableWrapperRect?.width ?? 0"
              [triggerOn]="blazeTableBody.tableBodyElement()?.nativeElement"
              blazeSticky
            />
          </div>
        }
        <be-table-body
          #blazeTableBody="blazeTableBody"
          [data]="dataSourceOrderedData"
          [focus]="focus"
          [idColumnName]="idColumnName"
          [isHeaderFloating]="isHeaderFloating"
          [isVirtualScrollRegistered]="isVirtualScrollRegistered"
          [rowHeightBehavior]="rowHeightBehavior"
          [rowHeightPx]="rowHeightPx"
          [tableWrapperRect]="tableWrapperRect"
          [virtualScrollDisabled]="isPrintMode()"
        >
          <ng-template
            let-item
            let-index="index"
            let-isLast="isLast"
            let-dataRowIndex="dataRowIndex"
          >
            @let isGroupByRow = !!item?.__groupBy;
            <be-table-row
              #row
              blazeClickable
              class="row-size-{{ isGroupByRow ? 'small' : rowSize }} row-behavior-{{
                rowHeightBehavior
              }} row-actions-{{ rowActionsDisplayMode }}"
              [class.row-click-action-hover]="actionOnRowClick && index === actionOnHoverIndex"
              [actionOnRowClickAvailable]="!!actionOnRowClick"
              [actions]="visibleRowActions?.[index] ?? []"
              [actionsTpl]="rowActionsGrouped ? groupedActionsTpl : rowActionsTpl"
              [cellsEditConfig]="cellsEditConfig"
              [cellsEditErrors]="cellsEditErrors"
              [cellTemplates]="cellsMap"
              [formatters]="context.formatters"
              [groupColors]="groupsStyling.groupColors"
              [groupColsDefs]="elementDataActions.groupColsDefs"
              [idColumnName]="idColumnName"
              [isInInlineEditMode]="isInInlineEditMode"
              [isLastRow]="isLast"
              [isWrappedGroup]="item.__id && wrappedGroupsIds.has(item.__id)"
              [hasBulkActions]="hasBulkActions"
              [hasRowNumbering]="hasRowNumbering()"
              [rowHeightPx]="
                rowHeightBehavior === 'auto'
                  ? undefined
                  : isGroupByRow
                    ? groupingRowHeightPx
                    : rowHeightPx
              "
              [rowIndex]="index"
              [dataRowIndex]="
                dataRowIndex +
                (paginationProps.enabled ? paginationProps.pageIndex * paginationProps.pageSize : 0)
              "
              [rowItem]="item"
              [selected]="selection.isSelected(item)"
              [showActionIcons]="rowActionsHaveIcons"
              [showActionsOnHoverOnly]="rowActionsDisplayMode === 'hover'"
              [virtualScrollEnabled]="rowHeightBehavior === 'fixed' && !isPrintMode()"
              [virtualScrollMinBufferPx]="columnMinBufferPx"
              [virtualScrollMaxBufferPx]="columnMaxBufferPx"
              (cellEdit)="cellEditFromRow($event)"
              (cellMouseDownToggled)="cellMouseDownToggled($event)"
              (click)="onRowClick($event, item)"
              (hoverCellRow)="setActionOnHoverIndex($event)"
              (toggleSelection)="elementDataActions.toggleSelection(item, value, { select: true })"
              (toggleWrap)="wrapClicked($event)"
            />
          </ng-template>
        </be-table-body>
        @if (totalCount === 0) {
          <div class="cell-no-data" [style.width.px]="rowWidth()">
            <div class="cell-container">
              <div class="table-no-data-message">{{ noDataMessage }}</div>
            </div>
          </div>
        }
        <div
          style="
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
          "
        ></div>
      </div>
    </div>
  </div>

  @if (stickyFooterTriggerOn) {
    @if (isFooterFloating) {
      <div
        #scrollWrapper
        [blazeScrollSync]="footerScrollSync"
        [enabled]="isFooterFloating"
        [style.left.px]="tableWrapperRect?.x"
        [style.width.px]="tableWrapperRect?.width"
        class="sticky-scroll"
      >
        <div
          [blazeStickyPositionOffset]="footerWrapper.clientHeight"
          [scrollContainer]="scrollWrapper"
          [style.width.px]="rowWidth()"
          [triggerOn]="stickyFooterTriggerOn"
          blazeSticky
          stickTo="bottom"
        ></div>
      </div>
    }
    <div
      #footerWrapper
      [style.left.px]="isFooterFloating ? tableWrapperRect?.x : undefined"
      [style.max-width.px]="isFooterFloating ? tableWrapperRect?.width : undefined"
      [style.width.px]="isFooterFloating ? tableWrapperRect?.width : undefined"
      class="footer-wrapper"
    >
      <div
        (stickyChange)="isFooterFloating = $event.isSticky"
        [blazeStickyEnabled]="footerAlwaysVisible"
        [blazeStickyEndOffset]="bottombarEndOffset$ | async"
        [blazeStickyPositionOffset]="bottombarPositionOffset$ | async"
        [class.paginator-hidden]="!dataRowsCount"
        [scrollContainer]="footerWrapper"
        [triggerOn]="stickyFooterTriggerOn"
        blazeSticky
        class="paginator-container flex flex-nowrap items-end mt-16"
        stickTo="bottom"
      >
        @if (paginationProps.enabled || bulkSelectionTriggerLabel) {
          @if (bulkSelectionTriggerLabel) {
            <div class="{{ elementKeyRoot }} {{ elementKey }}">
              <button
                (click)="selectAllBulk()"
                blazeClickable
                class="bd-btn btn-bulk-select-all"
                data-cy="TableElement:SelectAllBulk"
              >
                {{ bulkSelectionTriggerLabel }}
              </button>
            </div>
          }
          @if (paginationProps.enabled) {
            <blaze-paginator
              (page)="onPage($event)"
              [backwardPaginationSupported]="paginationProps.backwardPaginationSupported"
              [hidePageCount]="!cachedTotalRowsCount"
              [labelOverride]="bulkSelectionLabel"
              [loading]="paginationPending$ | async"
              [pageSizeOptions]="paginationProps.countOptions"
              [paginationState]="paginationProps.state"
              [serverSide]="paginationProps.isServerSide"
              [sPageIndex]="paginationProps.pageIndex"
              [sPageSize]="paginationProps.pageSize"
              [sTotalCount]="totalCount ?? 0"
              blazeClickable
              class="ml-auto"
            />
          }
        }
      </div>
    </div>
  }
  <div #loadMore class="w-full h-1"></div>
</div>

<!-- #region cells-->

<!-- cell: text -->
<ng-template #textCellTpl let-item let-column="column">{{
  column ? (item | dataRowFieldValuePipe: column.name | formatValue: 'text') : ''
}}</ng-template>

<!-- cell: email -->
<ng-template #emailCellTpl let-item let-column="column">{{
  item | dataRowFieldValuePipe: column.name
}}</ng-template>

<!-- cell: currency -->
<ng-template #currencyCellTpl let-item let-column="column">{{
  item
    | dataRowFieldValuePipe: column.name
    | formatValue: 'currency' : { precision: column.precision, currency: column.currency }
}}</ng-template>

<!-- cell: number -->
<ng-template #numberCellTpl let-item let-column="column">{{
  item
    | dataRowFieldValuePipe: column.name
    | formatValue: 'number' : { precision: column.precision }
}}</ng-template>

<!-- cell: date -->
<ng-template #dateCellTpl let-item let-column="column">{{
  item
    | dataRowFieldValuePipe: column.name
    | formatValue: 'date' : { dateFormat: column.dateFormat }
}}</ng-template>

<!-- cell: datetime -->
<ng-template #datetimeCellTpl let-item let-column="column">{{
  item
    | dataRowFieldValuePipe: column.name
    | formatValue: 'datetime' : { dateTimeFormat: column.dateTimeFormat }
}}</ng-template>

<!-- cell: time -->
<ng-template #timeCellTpl let-item let-column="column">{{
  item | dataRowFieldValuePipe: column.name | formatValue: 'time'
}}</ng-template>

<!-- cell: checkbox -->
<ng-template
  #checkboxCellTpl
  let-item
  let-column="column"
  let-isGroupByColumn="isGroupByColumn"
  let-isGroupByRow="isGroupByRow"
>
  @let isChecked = isCheckboxCellChecked(item | dataRowFieldValuePipe: column.name);
  <mat-icon
    class="col-boolean"
    [class.invisible]="isGroupByRow && !isGroupByColumn"
    [class.checked]="isChecked"
    svgIcon="{{ isChecked ? 'icon_boolean_on_24' : 'icon_boolean_off_24' }}"
  />
</ng-template>

<!-- cell: image -->
<ng-template
  #imageCellTpl
  let-item
  let-column="column"
  let-columnIndex="columnIndex"
  let-tableCell="tableCell"
  let-rowNumber="rowNumber"
  let-isGroupByColumn="isGroupByColumn"
  let-isGroupByRow="isGroupByRow"
>
  @let value = item | dataRowFieldValuePipe: column.name;
  @let initialsColumnName = getColumnName(column.image?.initialsColumn);
  @let initialsColumnValue =
    initialsColumnName ? (item | dataRowFieldValuePipe: initialsColumnName) : '';

  <blaze-image-column-value
    (clickAction)="
      !isGroupByColumn &&
        !isGroupByRow &&
        cellEdit($event.event, column.name, column.id, rowNumber, tableCell, $event)
    "
    [emptyText]="column.image?.emptyText"
    [emptyThumbnail]="column.image?.emptyThumbnail"
    [fillVertically]="!column.image?.shape && rowHeightBehavior === 'auto'"
    [imageAlign]="column.align"
    [imageFormat]="column.format"
    [imageProps]="column.image"
    [initialsColumnValue]="initialsColumnValue"
    [initialsProps]="column.initials"
    [isMultipleValue]="isMultipleValueAllowed && isMultipleValueAllowed[column.id ?? column.name]"
    [useExternalClickAction]="
      !isGroupByColumn &&
      !isGroupByRow &&
      cellsEditConfig &&
      !!cellsEditConfig[column.id ?? column.name]
    "
    [value]="value"
    class="column-image"
  />
</ng-template>

<!-- cell: progressBar -->
<ng-template
  #progressBarCellTpl
  let-item
  let-column="column"
  let-columnIndex="columnIndex"
  let-tableCell="tableCell"
  let-rowNumber="rowNumber"
  let-isGroupByColumn="isGroupByColumn"
  let-isGroupByRow="isGroupByRow"
>
  @if (!isGroupByRow) {
    <blaze-inline-progress-bar
      [isColumnPercent]="column.name | isColumnPercentage: dataValue.schema"
      [config]="column.progressBar"
      [value]="item | dataRowFieldValuePipe: column.name"
      [extremes]="columnsExtremes()[column.name]"
    />
  }
</ng-template>

<!-- cell: percentage -->
<ng-template #percentageCellTpl let-item let-column="column" let-isGroupByRow="isGroupByRow">
  @if (!isGroupByRow) {
    {{
      item
        | dataRowFieldValuePipe: column.name
        | formatValue: 'percentage' : { precision: column.precision }
    }}
  }
</ng-template>

<!-- cell: heading -->
<ng-template #headingCellTpl let-item let-column="column">{{
  item | dataRowFieldValuePipe: column.name | formatValue: 'text'
}}</ng-template>

<!-- cell: paragraph -->
<!-- Keep this in one line! -->
<ng-template #paragraphCellTpl let-item let-column="column">{{
  item | dataRowFieldValuePipe: column.name | formatValue: 'text'
}}</ng-template>

<!-- cell: richText -->
<ng-template #richTextCellTpl let-item let-column="column">
  <span
    [innerHTML]="(item | dataRowFieldValuePipe: column.name) ?? '' | securityTrust: 'html'"
  ></span>
</ng-template>

<!-- cell: markdown -->
<ng-template #markdownCellTpl let-item let-column="column">
  <span [innerHTML]="(item | dataRowFieldValuePipe: column.name) ?? '' | markdown"></span>
</ng-template>

<!-- cell: email -->
<ng-template #emailCellTpl let-item let-column="column">
  <span (click)="columClicked('email', column.name, item?.[column.name])">{{
    item | dataRowFieldValuePipe: column.name
  }}</span>
</ng-template>

<!-- cell: url -->
<ng-template #urlCellTpl let-item let-column="column">
  <span (click)="columClicked('url', column.name, item?.[column.name])">{{
    item | dataRowFieldValuePipe: column.name
  }}</span>
</ng-template>

<!-- cell: phoneNumber -->
<ng-template #phoneNumberCellTpl let-item let-column="column">{{
  item | dataRowFieldValuePipe: column.name | formatValue: 'phoneNumber'
}}</ng-template>

<!-- cell: timezone -->
<ng-template #timezoneCellTpl let-item let-column="column">{{
  item | dataRowFieldValuePipe: column.name | formatTimeZone | async
}}</ng-template>

<!-- cell: duration -->
<ng-template #durationCellTpl let-item let-column="column">{{
  item?.[column.name] | formatValue: 'duration' : { durationFormat: column.duration || 'h:mm' }
}}</ng-template>

<!-- cell: zipcode -->
<ng-template #zipcodeCellTpl let-item let-column="column">{{
  item | dataRowFieldValuePipe: column.name | formatValue: 'text'
}}</ng-template>

<!-- cell: tag / link -->
<ng-template
  #tagCellTpl
  let-item
  let-column="column"
  let-columnIndex="columnIndex"
  let-tableCell="tableCell"
  let-rowNumber="rowNumber"
  let-isGroupByColumn="isGroupByColumn"
  let-isGroupByRow="isGroupByRow"
>
  <blaze-tags-column-value
    [value]="item | dataRowFieldValuePipe: column.name"
    [cellStyles]="cellStyles"
    [fitToContainer]="rowHeightBehavior === 'fixed'"
    [palette]="tagConfig[column?.id ?? column.originName ?? column.name]?.palette"
    [order]="tagConfig[column?.id ?? column.originName ?? column.name]?.order"
    [isMultipleValue]="isMultipleValueAllowed && isMultipleValueAllowed[column.id ?? column.name]"
    [useExternalClickAction]="
      !isGroupByColumn &&
      !isGroupByRow &&
      isInInlineEditMode &&
      cellsEditConfig &&
      !!cellsEditConfig[column.id ?? column.name]
    "
    [useExternalValueClickAction]="
      !isGroupByColumn &&
      !isGroupByRow &&
      linkCellValueClickConfig &&
      !!linkCellValueClickConfig[column.id ?? column.name]
    "
    (clickAction)="
      !isGroupByColumn &&
        !isGroupByRow &&
        tagColumnClicked($event.event, column.name, column.id, rowNumber, tableCell, $event)
    "
    (valueClickAction)="
      !isGroupByColumn &&
        !isGroupByRow &&
        linkCellValueClick($event, column.name, column.id, rowNumber)
    "
    class="min-h-[32px]"
  />
</ng-template>

<!-- cell: file -->
<ng-template
  #fileCellTpl
  let-item
  let-column="column"
  let-index="index"
  let-tableCell="tableCell"
  let-rowNumber="rowNumber"
  let-isGroupByColumn="isGroupByColumn"
  let-isGroupByRow="isGroupByRow"
>
  <blaze-file-column-value
    class="files"
    [ngClass]="{
      'justify-end': column.align === 'right',
      'justify-center': column.align === 'center',
    }"
    [cellStyles]="cellStyles"
    [fitToContainer]="rowHeightBehavior === 'fixed'"
    [value]="item | dataRowFieldValuePipe: column.name"
    [isSecure]="isFieldSecure(column.name)"
    [isMultipleValue]="isMultipleValueAllowed && isMultipleValueAllowed[column.id ?? column.name]"
    [useExternalClickAction]="
      !isGroupByColumn &&
      !isGroupByRow &&
      cellsEditConfig &&
      !!cellsEditConfig[column.id ?? column.name]
    "
    (clickAction)="
      !isGroupByColumn &&
        !isGroupByRow &&
        cellEdit($event.event, column.name, column.id, rowNumber, tableCell, $event)
    "
  />
</ng-template>

<!-- cell: signature -->
<ng-template #signatureCellTpl let-item let-column="column">
  <blaze-signature-column-value [signature]="$any(item | dataRowFieldValuePipe: column.name)" />
</ng-template>

<!-- cell: location -->
<ng-template #geopointCellTpl let-item let-column="column">{{
  item | dataRowFieldValuePipe: column.name | formatValue: 'geopoint'
}}</ng-template>

<!-- cell: secureText -->
<ng-template #secureTextCellTpl let-item let-column="column">{{
  item | dataRowFieldValuePipe: column.name | formatValue: 'secureText'
}}</ng-template>

<!-- #endregion -->

<ng-template #rowActionsTpl let-item let-actions="actions" let-isGroupByRow="isGroupByRow">
  @if (!isGroupByRow) {
    @for (action of actions; track $index) {
      @let showInlineLabel = action.showInlineLabel !== false;
      <button
        (click)="onAction({ event: $event, action, dataRow: item, actionId: action.actionId })"
        [attr.data-cy-ctx]="action.label"
        [blazeTooltip]="action.label"
        [class.flex-row-reverse]="action.iconPosition === 'right'"
        [class.icon-only]="!showInlineLabel && action.icon"
        [disabled]="action | isActionPending: elementDataActions.pendingActions()"
        [ngClass]="getActionClasses(action.styleSheetTarget ?? action.styleSheetId)"
        blazeClickable
        class="action-btn"
        data-cy="TableElement:rowAction"
      >
        <blaze-element-loading-indicator
          [isVisible]="action | isActionPending: elementDataActions.pendingActions()"
          [mode]="'theme'"
        />
        @if (action.icon) {
          <mat-icon [svgIcon]="action.icon" />
        }
        @if (showInlineLabel || !action.icon) {
          <span>{{ action.label | textLimit: 25 }}</span>
        }
      </button>
    }
  }
</ng-template>

<ng-template #headerActionsTpl let-item let-actions="actions" let-isGroupByRow="isGroupByRow">
  @if (!isGroupByRow) {
    @for (action of actions; track $index) {
      <button
        (click)="
          onAction({
            event: $event,
            action,
            isBulkAction: action.actionKind === 'bulk',
            actionId: action.actionId,
          })
        "
        [class.flex-row-reverse]="action.iconPosition === 'right'"
        [disabled]="
          (action.actionKind === 'bulk' && !selectionCount) ||
          (action
            | isActionDisabled
              : elementDataActions.pendingActions()
              : elementDataActions.disableActionWhenPending())
        "
        [ngClass]="getActionClasses(action.styleSheetTarget ?? action.styleSheetId)"
        attr.data-cy="TableElement:{{
          action.actionKind === 'bulk' ? 'bulkAction' : 'otherAction'
        }}:{{ action.label }}"
        blazeClickable
        blazeIconButton="bat"
        blazeIconButtonSize="xl"
        blazeTooltip="{{
          action.label +
            (action.actionKind === 'bulk' && !selectionCount
              ? (action.label ? ' - ' : '') + 'Select rows to enable actions'
              : '')
        }}"
        class="efh-action-btn relative"
      >
        <blaze-element-loading-indicator
          [isVisible]="
            action
              | isActionDisabled
                : elementDataActions.pendingActions()
                : elementDataActions.disableActionWhenPending()
          "
          [mode]="'theme'"
        />
        @if (action.icon) {
          <span class="icon">
            <mat-icon [svgIcon]="action.icon" />
          </span>
        }
        @if (action.showInlineLabel || !action.icon) {
          <span>{{ action.label | textLimit: 25 }}</span>
        }
      </button>
    }
  }
</ng-template>

<ng-template
  #groupedActionsTpl
  let-item
  let-actions="actions"
  let-cy="cy"
  let-actionsHaveIcons="actionsHaveIcons"
  let-isBulkAction="isBulkAction"
  let-isGroupByRow="isGroupByRow"
>
  @if (!isGroupByRow) {
    @if (isBulkAction) {
      <button
        [attr.data-cy]="cy"
        [matMenuTriggerFor]="menu"
        blazeClickable
        blazeIconButton="bat"
        blazeIconButtonSize="xl"
        class="efh-action-btn"
      >
        <mat-icon svgIcon="icon_more_h_24" />
      </button>
    } @else {
      <button blazeClickable blazeIconButton [matMenuTriggerFor]="menu" [attr.data-cy]="cy">
        <mat-icon svgIcon="icon_more_h_24" />
      </button>
    }
  }

  <mat-menu #menu="matMenu" class="{{ elementDropdownKey }} bd-menu-actions">
    <ng-template matMenuContent>
      @if (isBulkAction && !selectionCount && !!elementDataActions.bulkActions.length) {
        <div class="border-a-12 border-x-0 border-t-0 p-8 text-primary-darker"
          >Select rows to enable actions</div
        >
      }
      @for (action of actions; track $index) {
        <button
          mat-menu-item
          blazeClickable
          [disabled]="action.actionKind === 'bulk' && !selectionCount"
          (click)="
            onAction({
              event: $event,
              action,
              dataRow: item,
              isBulkAction: action.actionKind ? action.actionKind === 'bulk' : isBulkAction,
              actionId: action.actionId,
              menu,
            })
          "
        >
          @if (action.icon || actionsHaveIcons) {
            <mat-icon [svgIcon]="action.icon || 'empty'" />
          }
          <span>{{ action.label }}</span>
        </button>
      }
    </ng-template>
  </mat-menu>
</ng-template>
